import { DateTime } from 'luxon';

export function getUserTimezone() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.timezone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
}

export function formatInUserTimezone(date, timezone = null) {
  if (!date) return 'N/A';
  const tz = timezone || getUserTimezone();
  try {
    let dt;
    if (typeof date === 'string') {
      if (date.includes(' ') && !date.includes('T')) {
        date = date.replace(' ', 'T');
      }
      dt = DateTime.fromISO(date, { zone: 'UTC' });
    } else if (date instanceof Date) {
      dt = DateTime.fromJSDate(date, { zone: 'UTC' });
    } else {
      return 'N/A';
    }
    if (!dt.isValid) return 'N/A';
    return dt.setZone(tz).toFormat('MMM d, yyyy h:mm a');
  } catch {
    return 'N/A';
  }
}

export function formatDateInUserTimezone(date, timezone = null) {
  if (!date) return 'N/A';
  const tz = timezone || getUserTimezone();
  try {
    let dt;
    if (typeof date === 'string') {
      if (date.includes(' ') && !date.includes('T')) {
        date = date.replace(' ', 'T');
      }
      dt = DateTime.fromISO(date, { zone: 'UTC' });
    } else if (date instanceof Date) {
      dt = DateTime.fromJSDate(date, { zone: 'UTC' });
    } else {
      return 'N/A';
    }
    if (!dt.isValid) return 'N/A';
    return dt.setZone(tz).toFormat('MMM d, yyyy');
  } catch {
    return 'N/A';
  }
}

export function formatTimeInUserTimezone(date, timezone = null) {
  if (!date) return 'N/A';
  const tz = timezone || getUserTimezone();
  try {
    let dt;
    if (typeof date === 'string') {
      if (date.includes(' ') && !date.includes('T')) {
        date = date.replace(' ', 'T');
      }
      dt = DateTime.fromISO(date, { zone: 'UTC' });
    } else if (date instanceof Date) {
      dt = DateTime.fromJSDate(date, { zone: 'UTC' });
    } else {
      return 'N/A';
    }
    if (!dt.isValid) return 'N/A';
    return dt.setZone(tz).toFormat('h:mm a');
  } catch {
    return 'N/A';
  }
}

export function getTimeZoneOptions() {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Australia/Sydney'];
  }
}

export function detectUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Asia/Kolkata';
  }
}

/**
 * Convert the application's UTC date + UTC time fields into the user's
 * configured timezone and display them as: Aug 31, 2026 4:58 AM.
 *
 * The CRM stores appointment values in UTC. This helper is intentionally
 * separate from formatInUserTimezone() because the legacy database schema
 * stores demo_date and demo_time in two columns.
 */
export function formatUtcDateTimeParts(dateValue, timeValue, timezone = null) {
  if (!dateValue && !timeValue) return 'N/A';

  const tz = timezone || getUserTimezone();

  try {
    let datePart = dateValue == null ? '' : String(dateValue).trim();
    let timePart = timeValue == null ? '' : String(timeValue).trim();

    if (!datePart) return formatInUserTimezone(timePart, tz);

    // MySQL DATE/DATETIME values and ISO values are both accepted.
    // Extract only the UTC calendar date when the legacy field contains
    // an ISO datetime such as 2026-09-02T00:00:00.000Z.
    const dateMatch = datePart.match(/^(\d{4}-\d{2}-\d{2})/);
    const normalizedDate = dateMatch ? dateMatch[1] : datePart;

    // Time may arrive as HH:mm, HH:mm:ss, or an ISO datetime. Prefer the
    // explicit time field because that is how the current schema stores it.
    let normalizedTime = timePart;
    const timeMatch = timePart.match(/(?:T|\s)(\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)/);
    if (timeMatch) normalizedTime = timeMatch[1];
    if (!normalizedTime) normalizedTime = '00:00:00';
    if (/^\d{2}:\d{2}$/.test(normalizedTime)) normalizedTime += ':00';

    const dt = DateTime.fromISO(`${normalizedDate}T${normalizedTime}`, { zone: 'UTC' });
    if (!dt.isValid) return 'N/A';

    return dt.setZone(tz).toFormat('MMM d, yyyy h:mm a');
  } catch {
    return 'N/A';
  }
}

export function formatDateTimeValue(value, timezone = null) {
  return formatInUserTimezone(value, timezone);
}

